<template>
  <div
    v-resize.left
    class="ml-px bg-elev-modal-bg flex h-full min-w-[344px] overflow-y-auto max-h-[calc(100vh-10px)] tablet:min-w-[300px] tablet:max-w-[300px] max-w-[440px] flex-col gap-y-2"
  >
    <h3 class="sr-only select-none">
      Chat user aside
    </h3>

    <header class="flex items-center justify-between p-2 bg-elev-header-bg">
      <h4 class="p-2 text-heading-sb-18-24 text-fg-base-fg3">
        {{ $t('mainAside.profile') }}
      </h4>
      <wui-btn
        icon
        prepend-icon="closeRegularMdM"
        variant="neut"
        state="alpha"
        @click="emit('close')"
      />
    </header>
    <div
      v-if="!subscriber"
      class="align-items-center grid justify-items-center"
    >
      <p>{{ $t('common.externalContact') }}</p>
      <p>{{ $t('common.noInformation') }}</p>
    </div>
    <section
      v-else
      class="p-5 flex items-center gap-3 mb-[6px] bg-bg-base-bg1"
    >
      <contact-avatar
        class="h-[100px]! w-[100px]!"
        :contact="subscriber"
        :contact-type="subscriber.type"
      />
      <div>
        <p class="text-heading-m-18-24 text-fg-base-fg3">
          {{ subscriber.name }}
        </p>
      </div>
    </section>

    <div class="px-4 bg-bg-base-bg1 mb-1 flex items-center gap-[6px]">
      <div
        class="basis-[25%] cursor-pointer transition-all gap-1 bg-surf-neut-soft-def hover:bg-surf-neut-soft-hov active:bg-surf-neut-soft-pres py-[11px] px-[6px] rounded-16 flex flex-col items-center justify-center"
        @click="downloadVCard"
      >
        <wui-icon
          name="downloadM"
          class="text-surf-brand-base-def"
        />
        <p class="text-label-r-13-18 text-surf-brand-base-def text-center">
          {{ $t('mainAside.downloadVCard') }}
        </p>
      </div>
    </div>

    <wui-accordion
      v-if="subscriber"
      v-model="isProfileOpened"
    >
      <template #header>
        <div
          class="flex items-center px-4 m-[6px] gap-2 bg-elev-header-bg"
        >
          <h4 class="text-heading-m-14-20 text-fg-base-fg3">
            {{ $t('notify.info') }}
          </h4>
          <wui-icon
            name="expand"
            class="transition-all h-4! w-4!"
            :class="{
              'rotate-180': isProfileOpened,
              'rotate-0': !isProfileOpened
            }"
          />
        </div>
      </template>
      <template #content>
        <section class="bg-bg-base-bg1 px-4">
          <div
            v-if="subscriber.position"
            class="grid grid-cols-[100px_auto] items-center gap-x-1 mx-[6px] pt-[6px] pb-[10px] border-b border-b-bor-divider mb-1"
          >
            <p class="text-body-r-13-18 text-fg-base-fg22">
              {{ $t('common.position') }}
            </p>
            <p class="text-body-r-14-20 text-fg-base-fg3">
              {{ subscriber.position }}
            </p>
          </div>
          <div
            v-if="subscriber.internalNumber"
            class="grid grid-cols-[100px_auto] items-center gap-x-1 mx-[6px] pt-[6px] pb-[10px] border-b border-b-bor-divider mb-1"
          >
            <p class="text-body-r-13-18 text-fg-base-fg22">
              {{ $t('common.number') }}
            </p>
            <p class="text-body-r-14-20 text-fg-base-fg3">
              {{ subscriber.internalNumber }}
            </p>
          </div>
          <div
            v-if="subscriber.mobilePhone"
            class="grid grid-cols-[100px_auto] items-center gap-x-1 mx-[6px] pt-[6px] pb-[10px] border-b border-b-bor-divider mb-1"
          >
            <p class="text-body-r-13-18 text-fg-base-fg22">
              {{ $t('chat.mobNumber') }}
            </p>
            <p class="text-body-r-14-20 text-fg-base-fg3">
              {{ subscriber.mobilePhone }}
            </p>
          </div>

          <div
            v-if="subscriber.email"
            class="grid grid-cols-[100px_auto] items-center gap-x-1 mx-[6px] pt-[6px] pb-[10px] border-b border-b-bor-divider mb-1"
          >
            <p class="text-body-r-13-18 text-fg-base-fg22">
              {{ $t('common.mail') }}
            </p>
            <p class="text-body-r-14-20 text-fg-base-fg3">
              <a
                :href="`mailto:${subscriber.email}`"
                target="_blank"
              >
                {{ subscriber.email }}
                <wui-icon
                  :name="isCopied ? 'checkRegularM' : 'copyM'"
                  class="text-fg-base-fg3"
                  small
                  @click.prevent="copyEmailToClipboard"
                />
              </a>
            </p>
          </div>

          <div
            v-if="subscriber.organization"
            class="grid grid-cols-[100px_auto] items-center gap-x-1 mx-[6px] pt-[6px] pb-[10px] border-b border-b-bor-divider mb-1"
          >
            <p class="text-body-r-13-18 text-fg-base-fg22">
              {{ $t('common.organization') }}
            </p>
            <p class="text-body-r-14-20 text-fg-base-fg3">
              {{ subscriber.organization }}
            </p>
          </div>

          <div
            v-if="subscriber.organizationalUnit"
            class="grid grid-cols-[100px_auto] items-center gap-x-1 mx-[6px] pt-[6px] pb-[10px] border-b border-b-bor-divider mb-1"
          >
            <p class="text-body-r-13-18 text-fg-base-fg22">
              {{ $t('common.department') }}
            </p>
            <p class="text-body-r-14-20 text-fg-base-fg3">
              {{ subscriber.organizationalUnit }}
            </p>
          </div>

          <div
            v-if="subscriber.bDay"
            class="grid grid-cols-[100px_auto] items-center gap-x-1 mx-[6px] pt-[6px] pb-[10px] border-b border-b-bor-divider mb-1"
          >
            <p class="text-body-r-13-18 text-fg-base-fg22">
              {{ $t('common.dateOfBirth') }}
            </p>
            <p class="text-body-r-14-20 text-fg-base-fg3">
              {{ getBDay }}
            </p>
          </div>
        </section>
      </template>
    </wui-accordion>
  </div>
</template>

<script lang="ts" setup>
import { WuiBtn, WuiIcon, WuiAccordion } from '@wui/common-library'
import dayjs from 'dayjs'
import { toRefs, computed, ref } from 'vue'

import { ContactAvatar } from '@/entities/contact-avatar'

import { vResize } from '@/shared/directives/v-resize'
import { Room } from '@/shared/types/store'
import { BIRTH_DAY_FORMAT } from '@/shared/utils/constants'
import { downloadContactAsVCard } from '@/shared/utils/vcard'

const props = defineProps<{
  room: Room
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const { room } = toRefs(props)

const isProfileOpened = ref(true)
const isCopied = ref(false)

const subscriber = computed(() => {
  return room.value?.subscribers.values().next().value
})

const downloadVCard = () => {
  if (subscriber.value) {
    downloadContactAsVCard({
      ...subscriber.value,
      lastName: subscriber.value.lastName ?? subscriber.value.name,
    })
  }
}

const copyEmailToClipboard = () => {
  if (subscriber.value?.email) {
    navigator.clipboard.writeText(subscriber.value.email)
  }
  isCopied.value = true

  setTimeout(() => {
    isCopied.value = false
  }, 1000)
}

const getBDay = computed(() => {
  if (subscriber.value?.bDay) {
    return dayjs.utc(subscriber.value.bDay).tz().format(BIRTH_DAY_FORMAT)
  }
  return ''
})
</script>
